import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaPlus, FaMinus, FaMicrosoft, FaLinkedin } from 'react-icons/fa';
import {
  SiGoogle, SiOkta, SiSap, SiZoho, SiStripe, SiPaypal, SiRazorpay, SiCoursera, SiUdemy,
} from 'react-icons/si';
import { GrOracle } from 'react-icons/gr';
import { useAuth } from '../../contexts/AuthContext';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import ScrollReveal from './ScrollReveal';
import { useDemoModal } from './DemoModal';
import logoReversed from '../../assets/images/eb/logo-reversed.png';
import appIcon from '../../assets/images/eb/app-icon.png';
import sellImage from '../../assets/images/landing/sell-new.png';
import heroVideo from '../../assets/images/eb/hero.mp4';
import heroPoster from '../../assets/images/eb/hero.png';
import {
  HERO, MEET, JOBS, ROLES, DEPLOY, RESOURCES, INDUSTRIES,
  COMPARE, MOBILE, PARTNER, FAQS, DEMO_CTA,
} from './ebContent';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './landing-eb-links.css';


// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const icon = (name, tone = 'green') => iconCtx(`./${name}-${tone}.png`);

// Page for each Resources card / link on this page, keyed by the label in ebContent.js
const RESOURCE_ROUTES = {
  'Skills framework': '/resources/skills-framework',
  'Interaction gallery': '/resources/interaction-gallery',
  'Report gallery': '/resources/report-gallery',
  'Guides and glossary': '/resources/guides',
  Comparisons: '/resources/comparisons',
  'Client stories': '/resources/client-stories',
};

// Phone frame used in the Mobile app section.
//   platform: 'ios' (dynamic island) | 'android' (camera punch-hole)
//   dark:     dark header band (course player screen)
//   head:     optional content rendered inside the header band, under the status row
const Phone = ({ platform = 'ios', caption, dark = false, head = null, children }) => (
  <div className={`eb-phone-wrap is-${platform}`}>
    <div className="eb-phone">
      <div className={`eb-phone-screen${dark ? ' has-dark-head' : ''}`}>
        <div className="eb-phone-top">
          <div className="eb-phone-status">
            <span>9:41</span>
            <i className={platform === 'ios' ? 'eb-phone-island' : 'eb-phone-cam'} aria-hidden="true" />
            <span>●●● ▮</span>
          </div>
          {head}
        </div>
        <div className="eb-phone-body">{children}</div>
      </div>
    </div>
    <p className="eb-phone-caption mb-0">{caption}</p>
  </div>
);

// Brand marks available in react-icons. Workday, Darwinbox, BambooHR and Go1 have no
// icon there, so they fall back to initials until an official logo file is added.
const STACK_ICONS = {
  'Microsoft Entra ID': { Icon: FaMicrosoft, color: '#0078D4' },
  'Google Workspace': { Icon: SiGoogle, color: '#4285F4' },
  Okta: { Icon: SiOkta, color: '#007DC1' },
  'SAP SuccessFactors': { Icon: SiSap, color: '#0070F2' },
  'Oracle HCM': { Icon: GrOracle, color: '#C74634' },
  'Zoho People': { Icon: SiZoho, color: '#E42527' },
  Stripe: { Icon: SiStripe, color: '#635BFF' },
  PayPal: { Icon: SiPaypal, color: '#003087' },
  Razorpay: { Icon: SiRazorpay, color: '#0C2451' },
  'LinkedIn Learning': { Icon: FaLinkedin, color: '#0A66C2' },
  Coursera: { Icon: SiCoursera, color: '#0056D2' },
  'Udemy Business': { Icon: SiUdemy, color: '#A435F0' },
};

const initials = (name) => name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const StackRow = ({ items }) => (
  <div className="eb-logos">
    {items.map((name, i) => (
      <div key={name} className="eb-logo" title={name} style={{ marginTop: i % 3 === 1 ? 26 : i % 3 === 2 ? 10 : 0 }}>
        <div
          className="eb-logo-tile"
          aria-hidden="true"
          style={{ animationDuration: `${5.5 + (i % 4) * 0.7}s`, animationDelay: `${-i * 0.6}s` }}
        >
          {STACK_ICONS[name]
            ? React.createElement(STACK_ICONS[name].Icon, { size: 34, color: STACK_ICONS[name].color })
            : initials(name)}
        </div>
        <span>{name}</span>
      </div>
    ))}
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { openDemo: goDemo } = useDemoModal(); // "Book a demo" opens the demo form
  const [openFaq, setOpenFaq] = useState(0);
  const jobsRef = useRef(null);
  const indRef = useRef(null);
  const scrollRef = (ref, dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.min(460, el.clientWidth * 0.85), behavior: 'smooth' });
  };
  const scrollJobs = (dir) => scrollRef(jobsRef, dir);
  const scrollInd = (dir) => scrollRef(indRef, dir);

  // Logged-in users go straight to their dashboard (unchanged behaviour)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const roleStr = String(user.role).toLowerCase();
    const roleNum = parseInt(user.role, 10);
    if (roleStr === 'admin' || roleNum === 0) {
      navigate('/admin/dashboard', { replace: true });
    } else if (roleStr === 'user' || roleNum === 1) {
      navigate('/user/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Support deep links such as /#platform coming from other public pages
  useEffect(() => {
    if (!location.hash) { window.scrollTo(0, 0); return undefined; } // opening the home page starts at the top
    const t = window.setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.hash]);

  const scrollTo = (hash) => document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const marquee = [...HERO.industries, ...HERO.industries];

  return (
    <div className="landing-page eb min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* ── Hero ── */}
      <header className="eb-hero text-center">
        <Container fluid className="px-0">
          <p className="eb-eyebrow">{HERO.eyebrow}</p>
          <h1 className="eb-hero-title">{HERO.title}</h1>
          <p className="eb-lead eb-hero-sub">{HERO.subtitle}</p>
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            <button type="button" className="landing-btn-primary" onClick={goDemo}>
              {HERO.primaryCta}
            </button>
            <button type="button" className="landing-btn-secondary" onClick={() => scrollTo('#resources')}>
              {HERO.secondaryCta}
            </button>
          </div>

          <div className="eb-hero-media">
            <video
              src={heroVideo}
              poster={heroPoster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Colleagues using EthosBoard at work"
            />
            <div className="eb-hero-score text-start">
              <div className="d-flex justify-content-between align-items-baseline">
                <small>{HERO.readiness.label}</small>
                <span className="eb-grade">{HERO.readiness.grade}</span>
              </div>
              <div className="d-flex align-items-baseline gap-2">
                <b>{HERO.readiness.score}</b>
                <span className="eb-hero-score-max">/100</span>
              </div>
              <div className="eb-hero-score-bar"><i style={{ width: `${HERO.readiness.score}%` }} /></div>
              <p>{HERO.readiness.note}</p>
            </div>
          </div>
        </Container>
      </header>

      <section className="eb-trusted" aria-label="Industries we serve">
        <p className="eb-trusted-label">{HERO.trustedLabel}</p>
        <div className="eb-marquee">
          <div className="eb-track" aria-hidden="true">
            {marquee.map((name, i) => <span key={`${name}-${i}`}>{name}</span>)}
          </div>
        </div>
      </section>

      {/* ── Meet EthosBoard ── */}
      <section id="platform" className="eb-section eb-section-cream eb-meet">
        <Container className="eb-meet-inner">
          <h2 className="eb-h2 reveal reveal-up">{MEET.eyebrow}</h2>

          <div className="eb-mock-stage reveal reveal-up">
            <div className="eb-float">
              <div className="eb-mock">
                <aside className="eb-mock-side" aria-hidden="true">
                  <img src={logoReversed} alt="" />
                  {MEET.sideNav.map((label, i) => (
                    <span key={label} className={i === 0 ? 'is-on' : ''}>{label}</span>
                  ))}
                </aside>
                <div className="eb-mock-main">
                  <div className="eb-mock-head">
                    <div>
                      <h3>{MEET.greeting}</h3>
                      <p className="sub">{MEET.org}</p>
                    </div>
                    <span className="eb-mock-assign">{MEET.assignLabel}</span>
                  </div>
                  <div className="eb-kpis">
                    {MEET.kpis.map((k) => (
                      <div key={k.label} className="eb-kpi">
                        <small>{k.label}</small>
                        <span className="eb-kpi-val">
                          <b>{k.value}</b>{k.suffix && <i>{k.suffix}</i>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="eb-mock-grid">
                    <div className="eb-panel">
                      <h4>Readiness by team</h4>
                      {MEET.teams.map((t) => (
                        <div key={t.name} className="eb-bar-row">
                          <span>{t.name}</span>
                          <div className="eb-bar"><i style={{ width: `${t.score}%` }} /></div>
                          <b>{t.score}</b>
                        </div>
                      ))}
                    </div>
                    <div id="ethosai" className="eb-panel eb-brief">
                      <h4><img src={icon('bolt')} alt="" />{MEET.briefingTitle}</h4>
                      {MEET.briefing.map((b) => (
                        <div key={b.t} className="eb-brief-item"><b>{b.t}</b><span>{b.d}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="eb-float-shadow" aria-hidden="true" />
          </div>

          <p className="eb-lead eb-meet-intro">{MEET.intro}</p>
          <button type="button" className="eb-btn-forest" onClick={() => navigate('/platform')}>{MEET.cta}</button>
        </Container>
      </section>

      {/* ── One platform, four jobs ── */}
      <section className="eb-jobs-sec">
        <div className="eb-jobs-head reveal reveal-up">
          <div>
            <p className="eb-eyebrow">{JOBS.eyebrow}</p>
            <h2 className="eb-h2">{JOBS.title}</h2>
          </div>
          <div className="eb-jobs-nav">
            <button type="button" className="eb-arrow" aria-label="Previous" onClick={() => scrollJobs(-1)}>←</button>
            <button type="button" className="eb-arrow is-next" aria-label="Next" onClick={() => scrollJobs(1)}>→</button>
          </div>
        </div>
        <div className="eb-jobs-track" ref={jobsRef}>
          {JOBS.items.map((job, i) => (
            <article key={job.verb} className="eb-job reveal reveal-up">
              {i === 0 && (
                <div className="eb-job-visual is-dark" aria-hidden="true">
                  <span className="eb-job-tag">{JOBS.builder.tag}</span>
                  <div className="eb-job-draft">
                    <small>{JOBS.builder.from}</small>
                    {JOBS.builder.lines.map((l) => <span key={l}>{l}</span>)}
                    <span className="foot">{JOBS.builder.foot}</span>
                  </div>
                </div>
              )}
              {i === 1 && (
                <div className="eb-job-visual is-photo" aria-hidden="true">
                  <img src={sellImage} alt="" />
                  <div className="eb-job-badge">
                    <small>{JOBS.sell.tag}</small>
                    <strong>{JOBS.sell.name}</strong>
                    <span>{JOBS.sell.meta}</span>
                  </div>
                </div>
              )}
              {i === 2 && (
                <div className="eb-job-visual is-light" aria-hidden="true">
                  <span className="eb-job-tag">{JOBS.approvalsTag}</span>
                  {JOBS.approvals.map((a) => (
                    <div key={a.t} className="eb-appr">
                      <div><b>{a.t}</b><span>{a.d}</span></div>
                      <em>Approve</em>
                    </div>
                  ))}
                </div>
              )}
              {i === 3 && (
                <div className="eb-job-visual is-gradient" aria-hidden="true">
                  <span className="eb-job-tag">{JOBS.scaleTag}</span>
                  <div className="eb-chips">
                    {JOBS.scaleChips.map((c) => <span key={c} className="eb-chip">{c}</span>)}
                  </div>
                </div>
              )}
              <p><strong>{job.verb}</strong> <span>{job.text}</span></p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Roles ── */}
      <section className="eb-section eb-section-cream">
        <Container>
          <div className="text-center mb-4 reveal reveal-up">
            <h2 className="eb-h2 mb-3">{ROLES.title}</h2>
            <p className="eb-lead mx-auto mb-0" style={{ maxWidth: 640 }}>{ROLES.subtitle}</p>
          </div>
          <div className="eb-roles">
            {ROLES.items.map((r) => (
              <article key={r.role} className="eb-role reveal reveal-up">
                <div className="eb-role-icon"><img src={icon(r.icon)} alt="" /></div>
                <h3>{r.role}</h3>
                <p>{r.does}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Integrations and deployment ── */}
      <section id="deploy" className="eb-section eb-deploy-sec">
        <Container className="eb-deploy-inner">
          <div className="eb-deploy-head reveal reveal-up">
            <p className="eb-eyebrow">{DEPLOY.eyebrow}</p>
            <h2 className="eb-h2">
              {DEPLOY.title.map((line, i) => (
                <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>
              ))}
            </h2>
          </div>

          <div className="eb-stack-card">
            <StackRow items={DEPLOY.stackTop} />
            <div className="eb-stack-mid">
              <h3>{DEPLOY.stackTitle}</h3>
              <p>{DEPLOY.stackText}</p>
              <Link to="/pricing">{DEPLOY.planLink}</Link>
            </div>
            <StackRow items={DEPLOY.stackBottom} />
          </div>

          <div className="eb-secured">
            <h3>{DEPLOY.deployTitle}</h3>
            {DEPLOY.options.map((o) => (
              <div key={o.name} className="eb-secured-item reveal reveal-up">
                <img src={icon(o.icon)} alt="" />
                <div><b>{o.name}</b><span>{o.desc}</span></div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Resources: each card opens its own page ── */}
      <section id="resources" className="eb-res-sec">
        <div className="eb-res-head">
          <div className="eb-res-title">
            <span className="eb-res-eyebrow">{RESOURCES.eyebrow}</span>
            <h2 className="eb-res-h">{RESOURCES.title}</h2>
          </div>
          <span className="eb-res-hint">{RESOURCES.hint}</span>
        </div>
        <div className="eb-res-marquee">
          <div className="eb-res-track">
            {[...RESOURCES.items, ...RESOURCES.items].map((r, i) => {
              const hidden = i >= RESOURCES.items.length;
              const to = RESOURCE_ROUTES[r.name];
              return (
                <div
                  key={`${r.name}-${i}`}
                  className="eb-res-card"
                  role="link"
                  tabIndex={hidden ? -1 : 0}
                  aria-hidden={hidden ? 'true' : undefined}
                  onClick={() => navigate(to)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(to); }}
                >
                  <span className="ic"><img src={icon(r.icon, 'cream')} alt="" /></span>
                  <span className="txt"><strong>{r.name}</strong><span>{r.desc}</span></span>
                  <span className="arrow" aria-hidden="true">→</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section id="industries" className="eb-ind-sec">
        <div className="eb-ind-head reveal reveal-up">
          <p className="eb-eyebrow">{INDUSTRIES.eyebrow}</p>
          <h2 className="eb-h2">
            {INDUSTRIES.title.map((line, i) => (
              <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>
            ))}
          </h2>
        </div>
        <div className="eb-ind-track" ref={indRef}>
          {INDUSTRIES.items.map((ind) => (
            <article key={ind.name} className="eb-ind-card reveal reveal-up">
              <div className="eb-ind-photo">
                {ind.photo ? (
                  <img className="photo" src={ind.photo} alt="" />
                ) : (
                  <div className="eb-ind-ph" aria-hidden="true"><img src={icon('image')} alt="" /></div>
                )}
                <span className="eb-ind-wave">{ind.wave}</span>
              </div>
              <h3>{ind.name}</h3>
              <p>{ind.line}</p>
            </article>
          ))}
        </div>
        <div className="eb-ind-foot">
          <Link to="/industries">{INDUSTRIES.allLabel}</Link>
          <div className="eb-jobs-nav">
            <button type="button" className="eb-arrow" aria-label="Previous" onClick={() => scrollInd(-1)}>←</button>
            <button type="button" className="eb-arrow is-next" aria-label="Next" onClick={() => scrollInd(1)}>→</button>
          </div>
        </div>
      </section>

      {/* ── Completion is not readiness ── */}
      <section className="eb-section">
        <Container style={{ maxWidth: 1000 }}>
          <div className="text-center mb-4 reveal reveal-up">
            <h2 className="eb-h2 mb-3">{COMPARE.title}</h2>
            <p className="eb-lead mx-auto mb-3" style={{ maxWidth: 680 }}>{COMPARE.subtitle}</p>
            <Link to="/resources/comparisons" className="eb-text-link">Read the Comparisons</Link>
          </div>
          <div className="eb-compare reveal reveal-up" role="table" aria-label="EthosBoard compared with a typical LMS or LXP">
            <div className="eb-compare-row head" role="row">
              <span role="columnheader" />
              <span role="columnheader">{COMPARE.colA}</span>
              <span role="columnheader">{COMPARE.colB}</span>
            </div>
            {COMPARE.rows.map((row) => (
              <div key={row.k} className="eb-compare-row" role="row">
                <span className="k" role="rowheader">{row.k}</span>
                <span className="a" role="cell">{row.a}</span>
                <span className="b" role="cell"><img src={icon('check')} alt="" />{row.b}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Mobile app ── */}
      <section id="mobile" className="eb-section eb-section-dark">
        <Container>
          <div className="text-center mb-4 reveal reveal-up">
            <p className="eb-eyebrow on-dark">{MOBILE.eyebrow}</p>
            <h2 className="eb-h2 mb-3">{MOBILE.title}</h2>
            <p className="eb-lead mx-auto mb-0" style={{ maxWidth: 620 }}>{MOBILE.text}</p>
          </div>

          <div className="eb-phones reveal reveal-up">
            {/* iOS · Learner */}
            <Phone platform="ios" caption="iOS · LEARNER">
              <div className="eb-phone-title">
                <img src={appIcon} alt="" width="26" height="26" />
                Today
              </div>
              <div className="eb-phone-card">
                <span className="eb-phone-label">MY READINESS</span>
                <div className="eb-phone-score">84<small>/100</small></div>
                <div className="eb-bar"><i style={{ width: '84%' }} /></div>
              </div>
              <div className="eb-phone-card"><b>Excursion response</b><span>SCORM · 12 min · due Fri</span></div>
              <div className="eb-phone-card"><b>Knowledge check</b><span>6 questions</span></div>
              <div className="eb-phone-btn mt-auto">Ask Learning Buddy</div>
            </Phone>

            {/* Android · Course player */}
            <Phone
              platform="android"
              dark
              caption="ANDROID · COURSE PLAYER"
              head={(
                <div className="eb-phone-head">
                  <span className="eb-phone-label">LESSON 3 OF 6</span>
                  <h4>Excursion response</h4>
                  <div className="eb-phone-prog"><i style={{ width: '50%' }} /></div>
                </div>
              )}
            >
              <div className="eb-phone-scorm">SCORM CONTENT</div>
              <p className="eb-phone-q">A reading is above 8°C. What first?</p>
              <div className="eb-phone-opt is-on">Quarantine and log the batch</div>
              <div className="eb-phone-opt">Move it to another fridge</div>
              <div className="eb-phone-btn mt-auto">Next</div>
            </Phone>

            {/* iOS · Manager */}
            <Phone platform="ios" caption="iOS · MANAGER">
              <div className="eb-phone-title">My team · 14 people</div>
              {MOBILE.team.map((t) => (
                <div key={t.n} className="eb-phone-member">
                  <div>
                    <span>{t.n}</span>
                    <div className="eb-bar"><i style={{ width: `${t.s}%` }} /></div>
                  </div>
                  <b>{t.s}</b>
                </div>
              ))}
              <div className="eb-phone-card eb-phone-exception mt-auto">
                <b>Deadline exception</b>
                <span>Omar requests 5 more days</span>
                <div className="eb-phone-btn is-sm">Approve</div>
              </div>
            </Phone>

            {/* Android · EthosAI */}
            <Phone platform="android" caption="ANDROID · ETHOSAI">
              <div className="eb-phone-title">
                <img src={icon('bolt')} alt="" width="18" height="18" />
                EthosAI
              </div>
              <div className="eb-phone-msg is-user">Which team is least ready?</div>
              <div className="eb-phone-msg is-ai">
                Retail, at <b>69</b>. Fire safety refresher is overdue for 22 people. I drafted 7 nudges.
              </div>
              <div className="eb-phone-card eb-phone-team">
                <div className="eb-bar-row" style={{ gridTemplateColumns: '1fr 24px' }}>
                  <span>Retail</span><b>69</b>
                </div>
                <div className="eb-bar"><i style={{ width: '69%' }} /></div>
              </div>
              <div className="eb-phone-composer mt-auto">
                <div className="eb-phone-ask">Ask anything…</div>
                <div className="eb-phone-btn is-sm">Review</div>
              </div>
            </Phone>
          </div>
        </Container>
      </section>

      {/* ── Partner ── */}
      <section id="partner" className="eb-section pb-0">
        <Container>
          <div className="eb-partner reveal reveal-up">
            <div className="eb-partner-media">
              {PARTNER.photo ? (
                <img src={PARTNER.photo} alt={PARTNER.photoAlt} />
              ) : (
                <div className="eb-partner-ph">
                  <img src={icon('image')} alt="" />
                  <span>{PARTNER.photoCaption}</span>
                </div>
              )}
            </div>
            <div className="eb-partner-copy">
              <p className="eb-eyebrow">{PARTNER.title}</p>
              <h2 className="eb-h2">{PARTNER.text1}</h2>
              <p className="eb-lead">{PARTNER.text2}</p>
              <Link to="/company">{PARTNER.link}</Link>
            </div>
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="eb-section">
        <Container style={{ maxWidth: 860 }}>
          <h2 className="eb-h2 text-center mb-4 reveal reveal-up">FAQs</h2>
          {FAQS.map(({ q, a }, i) => {
            const open = openFaq === i;
            return (
              <div key={q} className="eb-faq-item">
                <button
                  type="button"
                  className="eb-faq-q"
                  aria-expanded={open}
                  aria-controls={`eb-faq-${i}`}
                  onClick={() => setOpenFaq(open ? -1 : i)}
                >
                  {q}
                  <span className="eb-faq-sign">{open ? <FaMinus size={12} /> : <FaPlus size={12} />}</span>
                </button>
                {open && <div id={`eb-faq-${i}`} className="eb-faq-a">{a}</div>}
              </div>
            );
          })}
        </Container>
      </section>

      {/* ── Demo CTA ── */}
      <section id="demo" className="eb-section">
        <Container>
          <div className="eb-cta-card text-center reveal reveal-up">
            <div className="eb-cta-mark" aria-hidden="true"><i /><i /><i /></div>
            <h2 className="eb-h2">{DEMO_CTA.title}</h2>
            <p className="eb-lead">
              {DEMO_CTA.text} {DEMO_CTA.explore}{' '}
              {DEMO_CTA.links.map((label, i) => (
                <React.Fragment key={label}>
                  {i === 1 && ', the '}
                  {i === 2 && ', or '}
                  <Link to={RESOURCE_ROUTES[label] || '/resources/skills-framework'}>{label}</Link>
                </React.Fragment>
              ))}
              .
            </p>
            <button type="button" className="landing-btn-primary" onClick={goDemo}>
              {DEMO_CTA.button}
            </button>
          </div>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
